'use client'

import { PostOrderContextProvider } from '@lastbrand/post-order-web'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'

import { useQuinceNprogressRouter } from '@src/modules/navigation'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

interface PostOrderProviderWrapperProps {
  children: React.ReactNode
}

// Translation namespaces for post-order components
const COMMON_NAMESPACE = 'common'
const POST_ORDER_NAMESPACE = 'postOrder'
const POST_ORDER_PROVIDER_NAMESPACES = [POST_ORDER_NAMESPACE, COMMON_NAMESPACE] as const

/**
 * Wrapper component that provides PostOrderContextProvider only where needed,
 * instead of wrapping the entire app layout. This reduces initial load size
 * for pages that don't use post-order features.
 */
export const PostOrderProviderWrapper: React.FC<PostOrderProviderWrapperProps> = ({ children }) => {
  const nprogressRouter = useQuinceNprogressRouter()
  // Call useTranslation with both 'common' and 'accountPage' namespaces
  const { t } = useTranslation(POST_ORDER_PROVIDER_NAMESPACES)

  // Create a router adapter that matches the expected interface
  const routerAdapter = useMemo(
    () => ({
      push: (to: string) => {
        nprogressRouter.push(to)
      },
      replace: (to: string) => {
        nprogressRouter.replace(to)
      },
      back: () => {
        nprogressRouter.back()
      },
      forward: () => {
        nprogressRouter.forward()
      },
    }),
    [nprogressRouter],
  )

  // Create a wrapper function that returns the translation function with both namespaces
  // This will be called by usePostOrderTranslation hook
  const useTranslationWrapper = useMemo(() => () => ({ t }), [t])

  return (
    <PostOrderContextProvider
      nextRouter={routerAdapter}
      useTranslation={useTranslationWrapper}
      isDesignSystemEnabled={shouldUseDesignSystem()}
    >
      {children}
    </PostOrderContextProvider>
  )
}

export default PostOrderProviderWrapper
